import React, { useEffect, useMemo, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  List,
  ListItemButton,
  ListItemText,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { useSocket } from '../../context/SocketContext'
import { getAuthToken } from '../../services/api'
import { listConversations, listMessages, sendMessage } from '../../services/chatService'

const Chat = () => {
  const { socket, connected, initializeSocket } = useSocket()
  const [conversations, setConversations] = useState([])
  const [selectedConversation, setSelectedConversation] = useState(null)
  const [messages, setMessages] = useState([])
  const [messageBody, setMessageBody] = useState('')
  const [loading, setLoading] = useState(true)
  const [sending, setSending] = useState(false)
  const [error, setError] = useState('')

  const selectedConversationId = selectedConversation?.id

  const activeConversationTitle = useMemo(() => {
    if (!selectedConversation) return 'Selecciona una conversación'
    return `Conversación #${selectedConversation.id} (${selectedConversation.type})`
  }, [selectedConversation])

  useEffect(() => {
    const loadConversations = async () => {
      if (!getAuthToken()) {
        setLoading(false)
        return
      }
      try {
        setLoading(true)
        const data = await listConversations()
        setConversations(data)
        setSelectedConversation(data[0] || null)
      } catch (apiError) {
        setError(apiError?.response?.data?.message || 'No se pudo cargar el chat')
      } finally {
        setLoading(false)
      }
    }

    loadConversations()
  }, [])

  useEffect(() => {
    const loadMessages = async () => {
      if (!selectedConversationId) {
        setMessages([])
        return
      }
      try {
        const data = await listMessages(selectedConversationId, { limit: 50 })
        setMessages(data)
      } catch (apiError) {
        setError(apiError?.response?.data?.message || 'No se pudieron cargar mensajes')
      }
    }

    loadMessages()
  }, [selectedConversationId])

  useEffect(() => {
    const activeSocket = socket || initializeSocket()
    if (!activeSocket || !selectedConversationId) return undefined

    const roomPayload = { conversationId: selectedConversationId }

    const joinConversation = () => {
      activeSocket.emit('chat:join', roomPayload)
    }

    const onJoined = ({ conversationId }) => {
      if (Number(conversationId) === Number(selectedConversationId)) {
        setError('')
      }
    }

    const onMessage = (payload) => {
      const incomingConversationId = Number(payload?.conversationId)
      if (incomingConversationId !== Number(selectedConversationId)) {
        return
      }

      const incomingMessage = payload?.message
      if (!incomingMessage) return

      setMessages((prev) => {
        if (prev.some((item) => String(item._id) === String(incomingMessage._id))) {
          return prev
        }
        return [...prev, incomingMessage]
      })
    }

    const onChatError = (payload) => {
      if (
        payload?.conversationId &&
        Number(payload.conversationId) !== Number(selectedConversationId)
      ) {
        return
      }
      setError(payload?.message || 'Error de socket en chat')
    }

    const onMessageNew = (message) => {
      onMessage({ conversationId: selectedConversationId, message })
    }

    activeSocket.on('chat:joined', onJoined)
    activeSocket.on('chat:message', onMessage)
    activeSocket.on('chat:message:new', onMessageNew)
    activeSocket.on('chat:error', onChatError)
    activeSocket.on('connect', joinConversation)

    joinConversation()

    return () => {
      activeSocket.emit('chat:leave', roomPayload)
      activeSocket.off('chat:joined', onJoined)
      activeSocket.off('chat:message', onMessage)
      activeSocket.off('chat:message:new', onMessageNew)
      activeSocket.off('chat:error', onChatError)
      activeSocket.off('connect', joinConversation)
    }
  }, [socket, initializeSocket, selectedConversationId])

  const handleSendMessage = async () => {
    if (!selectedConversationId || !messageBody.trim()) {
      return
    }

    const trimmedBody = messageBody.trim()

    try {
      setSending(true)
      setError('')

      const activeSocket = socket || initializeSocket()
      if (activeSocket?.connected) {
        activeSocket.emit('chat:send', {
          conversationId: selectedConversationId,
          body: trimmedBody,
        })
      } else {
        await sendMessage(selectedConversationId, { body: trimmedBody })
      }

      setMessageBody('')
    } catch (apiError) {
      setError(apiError?.response?.data?.message || 'No se pudo enviar el mensaje')
    } finally {
      setSending(false)
    }
  }

  return (
    <Stack direction="row" spacing={2} sx={{ p: 2, height: 'calc(100vh - 140px)' }}>
      <Box sx={{ width: 320, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ p: 2 }}>
          <Typography variant="h6">Conversaciones</Typography>
          <Chip
            size="small"
            label={connected ? 'Socket online' : 'Socket offline'}
            color={connected ? 'success' : 'default'}
            variant={connected ? 'filled' : 'outlined'}
          />
        </Stack>
        <Divider />
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
            <CircularProgress size={24} />
          </Box>
        ) : (
          <List dense>
            {conversations.map((conversation) => (
              <ListItemButton
                key={conversation.id}
                selected={selectedConversationId === conversation.id}
                onClick={() => setSelectedConversation(conversation)}
              >
                <ListItemText
                  primary={`#${conversation.id} - ${conversation.type}`}
                  secondary={conversation.status}
                />
              </ListItemButton>
            ))}
          </List>
        )}
      </Box>

      <Box
        sx={{
          flex: 1,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Typography variant="h6" sx={{ p: 2 }}>
          {activeConversationTitle}
        </Typography>
        <Divider />

        {error ? (
          <Alert severity="error" sx={{ m: 2 }}>
            {error}
          </Alert>
        ) : null}

        <Box sx={{ flex: 1, p: 2, overflowY: 'auto' }}>
          {messages.map((message) => (
            <Box key={message._id || `${message.conversationId}-${message.createdAt}`} sx={{ mb: 1 }}>
              <Typography variant="caption" color="text.secondary">
                {message.senderType} #{message.senderId}
              </Typography>
              <Typography>{message.body}</Typography>
            </Box>
          ))}
        </Box>

        <Divider />
        <Stack direction="row" spacing={1} sx={{ p: 2 }}>
          <TextField
            fullWidth
            size="small"
            value={messageBody}
            onChange={(event) => setMessageBody(event.target.value)}
            placeholder="Escribe un mensaje"
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault()
                handleSendMessage()
              }
            }}
          />
          <Button
            variant="contained"
            onClick={handleSendMessage}
            disabled={sending || !messageBody.trim()}
          >
            Enviar
          </Button>
        </Stack>
      </Box>
    </Stack>
  )
}

export default Chat
